import { useState } from 'react'

import { Select, Text, Switch, TextInput, Loader } from '@mantine/core'

import { useQuery, useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_JOB_LISTING_QUESTION_TEMPLATES,
  ADD_JOB_LISTING_QUESTION_TEMPLATE,
  REORDER_JOB_LISTING_QUESTION_TEMPLATES,
} from 'src/graphql/joblistingsQuestionTemplates.graphql'
import { GET_VOICE_AGENTS } from 'src/graphql/voiceAgents.graphql'
import SettingsField from 'src/pages/SettingsPage/components/SettingsField'

import JobQuestionTemplate from './JobQuestionTemplate'

const OPERATOR_OPTIONS = [
  { value: 'EQUALS', label: 'Equals' },
  { value: 'NOT_EQUALS', label: 'Not equals' },
  { value: 'GT', label: 'Greater than' },
  { value: 'LT', label: 'Less than' },
  { value: 'GTE', label: 'Greater than or equal to' },
  { value: 'LTE', label: 'Less than or equal to' },
  { value: 'IS_TRUE', label: 'Is true' },
  { value: 'IS_FALSE', label: 'Is false' },
]

interface JobQuestionsSection {
  jobListingId: string
}

const JobQuestionsSection = ({ jobListingId }: JobQuestionsSection) => {
  const [filterVoiceAgent, setFilterVoiceAgent] = useState('')
  const [selectedQuestionTemplate, setSelectedQuestionTemplate] = useState('')
  const [isDisqualifier, setIsDisqualifier] = useState(false)
  const [operator, setOperator] = useState('')
  const [comparisonValue, setComparisonValue] = useState('')
  const [isAddingQuestionTemplate, setIsAddingQuestionTemplate] =
    useState(false)

  // Queries
  const { data: voiceAgentsData, loading: voiceAgentsLoading } = useQuery(
    GET_VOICE_AGENTS,
    {
      variables: { pageInfo: { limit: 100, offset: 0 } },
    }
  )

  const {
    data: questionTemplatesData,
    loading: _questionTemplatesLoading,
    refetch: refetchQuestionTemplates,
  } = useQuery(GET_JOB_LISTING_QUESTION_TEMPLATES, {
    variables: {
      jobListingId,
      voiceAgentId: filterVoiceAgent,
    },
    skip: !filterVoiceAgent,
  })

  // Mutations
  const [addQuestionTemplate] = useMutation(ADD_JOB_LISTING_QUESTION_TEMPLATE, {
    onCompleted: () => {
      toast('Question template added successfully', 'success')
      setIsAddingQuestionTemplate(false)
      resetForm()
      refetchQuestionTemplates()
    },
    onError: (error) => {
      toast(`Failed to add question template: ${error.message}`, 'error')
    },
  })

  const [reorderQuestionTemplates] = useMutation(
    REORDER_JOB_LISTING_QUESTION_TEMPLATES,
    {
      onCompleted: () => {
        toast('Questions reordered successfully', 'success')
        refetchQuestionTemplates()
      },
      onError: (error) => {
        toast(`Failed to reorder questions: ${error.message}`, 'error')
      },
    }
  )

  const voiceAgents = voiceAgentsData?.voiceAgents?.items || []
  const jobQuestionTemplates =
    questionTemplatesData?.jobListingQuestionTemplates || []

  // Get available question templates for the selected voice agent (when adding)
  const selectedVoiceAgentData = voiceAgents.find(
    (agent) => agent.voiceAgentId === filterVoiceAgent
  )
  const availableQuestionTemplates =
    selectedVoiceAgentData?.questionTemplates || []

  // Filter out already added question templates
  const unusedQuestionTemplates = availableQuestionTemplates.filter(
    (template) =>
      !jobQuestionTemplates.some(
        (jqt) => jqt.questionTemplateId === template.questionTemplateId
      )
  )

  const resetForm = () => {
    setSelectedQuestionTemplate('')
    setIsDisqualifier(false)
    setOperator('')
    setComparisonValue('')
  }

  const handleAddQuestionTemplate = () => {
    if (!filterVoiceAgent || !selectedQuestionTemplate) {
      toast('Please select a voice agent and question template', 'error')
      return
    }

    addQuestionTemplate({
      variables: {
        input: {
          jobListingId,
          voiceAgentId: filterVoiceAgent,
          questionTemplateId: selectedQuestionTemplate,
          disqualifier: isDisqualifier,
          operator: operator || null,
          comparisonValue: comparisonValue || null,
        },
      },
    })
  }

  // Reorder handler for up/down buttons (now voice-agent specific)
  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (
      toIndex < 0 ||
      toIndex >= jobQuestionTemplates.length ||
      fromIndex === toIndex
    ) {
      return
    }

    const items = [...jobQuestionTemplates]
    const [movedItem] = items.splice(fromIndex, 1)
    items.splice(toIndex, 0, movedItem)

    // Since we're already filtering by voice agent, all items belong to the same voice agent
    if (items.length > 0 && items[0]?.voiceAgentId) {
      reorderQuestionTemplates({
        variables: {
          input: {
            jobListingId,
            voiceAgentId: items[0].voiceAgentId,
            questionTemplateIds: items.map((item) => item.questionTemplateId),
          },
        },
      })
    }
  }

  if (voiceAgentsLoading) {
    return (
      <div className="flex items-center justify-center">
        <Loader size="xs" color="gray" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto mt-3">
      <SettingsField
        title="Voice Agent"
        description="Select a voice agent to view its questions"
      >
        <Select
          placeholder="All Voice Agents"
          size="xs"
          data={voiceAgents.map((agent) => ({
            value: agent.voiceAgentId,
            label: agent.name,
          }))}
          value={filterVoiceAgent}
          onChange={(value) => setFilterVoiceAgent(value || '')}
          searchable
          clearable
        />
      </SettingsField>

      {jobQuestionTemplates.length > 0 ? (
        <div>
          {jobQuestionTemplates.map((jqt, index) => {
            return (
              <JobQuestionTemplate
                key={jqt.questionTemplateId}
                jobListingId={jobListingId}
                jobQuestionTemplate={jqt}
                handleReorder={handleReorder}
                index={index}
                templatesLength={jobQuestionTemplates?.length}
                refetchQuestionTemplates={refetchQuestionTemplates}
              />
            )
          })}
        </div>
      ) : (
        <Text size="xs" className="py-6 text-center">
          {filterVoiceAgent
            ? 'No question templates found for the selected voice agent.'
            : 'Select a voice agent to view its questions.'}
        </Text>
      )}

      {isAddingQuestionTemplate ? (
        <div className="flex flex-col gap-4">
          <SettingsField
            title="Question Template"
            description="Select a question template to add"
          >
            <Select
              placeholder="Question Template"
              nothingFoundMessage="No questions found"
              size="xs"
              data={unusedQuestionTemplates.map((template) => ({
                value: template.questionTemplateId,
                label:
                  template.shortQuestionName ||
                  template.question.substring(0, 50) + '...',
              }))}
              value={selectedQuestionTemplate}
              onChange={(value) => setSelectedQuestionTemplate(value || '')}
              searchable
              disabled={!filterVoiceAgent}
            />
          </SettingsField>

          <SettingsField
            title="Disqualifier"
            description="Is this a disqualifier question?"
          >
            <Switch
              size="xs"
              checked={isDisqualifier}
              onChange={(e) => setIsDisqualifier(e.currentTarget.checked)}
            />
          </SettingsField>
          {isDisqualifier && (
            <>
              <SettingsField title="Operator" description="Select an operator">
                <Select
                  w={180}
                  placeholder="Operator"
                  data={OPERATOR_OPTIONS}
                  size="xs"
                  value={operator}
                  onChange={(value) => setOperator(value || '')}
                  clearable
                />
              </SettingsField>

              <SettingsField
                title="Comparison Value"
                description="Enter a comparison value"
              >
                <TextInput
                  placeholder="Comparison Value"
                  w={180}
                  size="xs"
                  value={comparisonValue}
                  onChange={(e) => setComparisonValue(e.target.value)}
                />
              </SettingsField>
            </>
          )}

          <div className="flex flex-row items-center justify-end gap-2">
            <DNButton
              variant="secondary"
              onClick={() => {
                setIsAddingQuestionTemplate(false)
                resetForm()
              }}
            >
              Cancel
            </DNButton>
            <DNButton
              variant="primary"
              onClick={handleAddQuestionTemplate}
              disabled={!filterVoiceAgent || !selectedQuestionTemplate}
            >
              Add Question
            </DNButton>
          </div>
        </div>
      ) : (
        filterVoiceAgent && (
          <div>
            <DNButton
              variant="secondary"
              onClick={() => setIsAddingQuestionTemplate(true)}
            >
              Add Question Template
            </DNButton>
          </div>
        )
      )}
    </div>
  )
}

export default JobQuestionsSection
